import type { TimelineEntry } from "@tdelivr/api-client/generated/support/support-timeline.js";
import type { StateClassKey } from "@tdelivr/design-tokens";

import { at, field, optional, type RecordDetail } from "./record-detail.ts";

/**
 * What the case timeline does to the entries SUP-03 returns, away from any rendering.
 *
 * **This is the closest match in this console between what is drawn and what is published.** The
 * design's three columns are `When`, `Source` and `Event`; SUP-03 publishes `occurredAt`, `source`
 * and `summary`, with `category` for the badge beside them and an optional `reference` for the
 * identifier the design appends — `authorisation sent to M-Pesa · pay_91QK`. Nothing in the grid is
 * approximated.
 *
 * It also publishes something no other listing on this platform does: a **`stale` flag and a
 * checkpoint per source**. CLAUDE.md section 1 requires the stale state be built, and on every
 * other board in this repository staleness has had to be inferred or recorded as absent. Here the
 * service answers it, which is why `stalenessOf` reads rather than computes.
 *
 * **The state vocabulary is the contract's `category`, which is an open string.** SUP-01's status is
 * an enum and this is not: the contract publishes `category: string` with no enumeration, so the
 * mapping below is over the values the design's own rows carry and anything else falls to the
 * design's `unknown` class rather than to a plausible-looking one. That is the treatment
 * `StateBadge` already refuses on — an unclassified badge is visible to whoever has to fix it.
 */

/**
 * How many entries to take in one request.
 *
 * SUP-03's own maximum, which is **100** — `z.coerce.number().int().min(1).max(100)` on both of
 * its timeline reads. This said so and then asked for 200, so every request this board made was
 * answered `400 request_validation_failed` with `too_big` on `limit`, and the board drew nothing
 * for any case.
 *
 * Measured 26 October by choosing a case in the queue and opening the timeline, which is the only
 * way to reach it: the selection is deliberately kept out of the address bar, so a page loaded
 * directly draws the designed empty state and looks fine. The board had been unreachable in a way
 * no sweep could see.
 *
 * 200 is the support console's page size elsewhere — `cases.ts`, `lookup.ts` and `goodwill.ts` all
 * use it, and for those services it is correct. A page size is **per service**, not per console,
 * and a constant that travels between them carries the wrong bound to the next one.
 *
 * The timeline is cursor-paged on `(sourceSequence, entryId)`, which is immutable per source — a
 * projection appends and never rewrites — so following the cursor cannot lose an entry or show one
 * twice. A smaller page costs an extra request on a long case and nothing else.
 */
export const PER_FETCH = 100;

/**
 * The state class and the word, from `category`.
 *
 * | category         | class     | word               | where the word comes from                   |
 * | ---------------- | --------- | ------------------ | ------------------------------------------- |
 * | `sent`           | `done`    | `sent`             | the design's `done · sent` row              |
 * | `shown`          | `done`    | `shown`            | the design's `done · shown` row             |
 * | `opened`         | `done`    | `opened`           | the design's `done · opened` row            |
 * | `waiting`        | `active`  | `waiting`          | the design's `active · waiting` row         |
 * | `outcome_unknown`| `unknown` | `outcome unknown`  | the design's `unknown · outcome unknown` row|
 * | `customer`       | `human`   | `customer message` | the design's `human · customer message` row |
 *
 * The underscored form and the spaced form are both accepted for the two that have one, because a
 * projection's `category` is whatever the emitting service wrote and this console does not get to
 * decide which convention that was.
 *
 * **`outcome_unknown` is `unknown` and never `failed`.** The design's `unknown` class is dashed and
 * grey and its stated rationale is that it "borrows neither success nor failure, because neither is
 * true yet" — and `07 Payment Outcome Unknown.dc.html` is a whole design file about this one state
 * on this exact case. The timeline's own designed rows are a payment whose provider did not answer;
 * drawing that as a failure is how an agent tells a customer they were not charged when they were.
 *
 * Anything else is `unknown` with the service's own word, unaltered. `category` is an open string on
 * the contract, so a value this table does not cover is a real possibility rather than a defect, and
 * a guess at its class would put a colour on a meaning nobody has decided.
 */
export function stateOf(entry: TimelineEntry): { classKey: StateClassKey; label: string } {
  switch (entry.category) {
    case "sent":
      return { classKey: "done", label: "sent" };
    case "shown":
      return { classKey: "done", label: "shown" };
    case "opened":
      return { classKey: "done", label: "opened" };
    case "waiting":
      return { classKey: "active", label: "waiting" };
    case "outcome_unknown":
    case "outcome unknown":
      return { classKey: "unknown", label: "outcome unknown" };
    case "customer":
    case "customer_message":
      return { classKey: "human", label: "customer message" };
    default:
      return { classKey: "unknown", label: entry.category };
  }
}

/**
 * The design's `Event` cell: the summary, then the reference where there is one.
 *
 * The design writes `authorisation sent to M-Pesa · pay_91QK` and `customer saw "checking with
 * M-Pesa"` — a sentence, and sometimes an identifier after it. `reference` is optional on the
 * contract for exactly that reason, so the separator is dropped rather than left dangling.
 */
export function eventCell(entry: TimelineEntry): string {
  return entry.reference === undefined || entry.reference === ""
    ? entry.summary
    : `${entry.summary} · ${entry.reference}`;
}

/**
 * The design's `When` cell: the time of day, to the second.
 *
 * The design's own column is `12:31:04` — a time and no date, because a case timeline is read
 * within a shift. The date is on the row's title, because a case open across midnight has two days
 * in it and a column showing only the time would silently reorder in a reader's head.
 *
 * UTC, stated. The design file writes times with no zone and the tenant chip's second line has no
 * timezone either (nothing publishes a tenant's), so a local rendering would be the browser's guess
 * about the tenant's working day. R-35.
 */
export function whenCell(instant: string): string {
  const parsed = Date.parse(instant);
  if (Number.isNaN(parsed)) return instant;
  return new Date(parsed).toISOString().slice(11, 19);
}

/**
 * Which sources are behind, from the checkpoints SUP-03 publishes beside the entries.
 *
 * The service answers `stale: boolean` and a checkpoint per source carrying `lastSequence` and
 * `projectedAt`. A board that drew only the flag would say "some of this may be behind" and leave
 * an agent unable to tell whether the part they are reading is the stale part — so the sources are
 * named, with how long ago each was last projected.
 *
 * Returns the empty list when nothing is behind, which is not the same as an empty timeline.
 */
export function stalenessOf(
  stale: boolean,
  checkpoints: readonly { source: string; projectedAt: string }[],
  now: number,
): readonly { source: string; behindBy: string }[] {
  if (!stale) return [];
  return checkpoints
    .map((checkpoint) => {
      const projected = Date.parse(checkpoint.projectedAt);
      const seconds = Number.isNaN(projected) ? undefined : Math.round((now - projected) / 1000);
      return {
        source: checkpoint.source,
        behindBy:
          seconds === undefined
            ? checkpoint.projectedAt
            : seconds < 90
              ? `${String(seconds)}s ago`
              : `${String(Math.round(seconds / 60))} min ago`,
      };
    })
    .sort((left, right) => left.source.localeCompare(right.source));
}

/** The sort options the design's toolbar offers, in its order and its words. */
export type TimelineOrder = "default" | "state" | "az" | "za";

export interface TimelineQuery {
  readonly search: string;
  readonly order: TimelineOrder;
  /** 1-based, as the pager shows it. */
  readonly page: number;
  readonly perPage: number;
}

export interface TimelineView {
  readonly rows: readonly TimelineEntry[];
  /** Every row that matched, across all pages — what an export writes. */
  readonly all: readonly TimelineEntry[];
  readonly total: number;
  readonly pageCount: number;
}

/** Every word on a row a search should match: the published values and the words on screen. */
function searchableText(entry: TimelineEntry): string {
  return [
    entry.entryId,
    entry.source,
    entry.summary,
    entry.reference ?? "",
    entry.category,
    stateOf(entry).label,
    whenCell(entry.occurredAt),
  ]
    .join(" ")
    .toLowerCase();
}

/**
 * Filter, order and page what the screen holds.
 *
 * `default` is **oldest first**, which is the design's own stated order — "Everything that happened
 * on case cs_4471, **oldest first**, across every system" — and the opposite of every other board
 * in this repository. A timeline read newest-first is a story told backwards, and the one thing a
 * reader of a case does is follow what happened.
 *
 * SUP-03 answers in its own order, so this sorts rather than trusting it: entries come from several
 * sources, each with its own sequence, and the one ordering that is true across all of them is the
 * instant each thing happened.
 */
export function timelinePage(
  rows: readonly TimelineEntry[],
  query: TimelineQuery,
  state?: string,
): TimelineView {
  const needle = query.search.trim().toLowerCase();
  const matched = rows.filter(
    (entry) =>
      (state === undefined || stateOf(entry).label === state) &&
      (needle.length === 0 || searchableText(entry).includes(needle)),
  );

  const ordered =
    query.order === "default"
      ? [...matched].sort(
          (left, right) =>
            Date.parse(left.occurredAt) - Date.parse(right.occurredAt) ||
            left.sourceSequence - right.sourceSequence,
        )
      : [...matched].sort((left, right) => {
          if (query.order === "state") {
            return stateOf(left).label.localeCompare(stateOf(right).label);
          }
          // "Name" is the first column, as the design's A–Z is. Here that is the instant.
          const compared = left.occurredAt.localeCompare(right.occurredAt);
          return query.order === "az" ? compared : -compared;
        });

  const total = ordered.length;
  const pageCount = Math.max(1, Math.ceil(total / query.perPage));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const from = (page - 1) * query.perPage;

  return { rows: ordered.slice(from, from + query.perPage), all: ordered, total, pageCount };
}

/** The states present in what came back, in board order, as the design's own `gridUI` does. */
export function statesPresent(rows: readonly TimelineEntry[]): readonly string[] {
  return [...new Set(rows.map((entry) => stateOf(entry).label))];
}

/** The systems this case's entries came from, which is the design's `Systems involved` sub-label. */
export function sourcesPresent(rows: readonly TimelineEntry[]): readonly string[] {
  return [...new Set(rows.map((entry) => entry.source))].sort();
}

/**
 * The sentence this board owes an agent about what it actually fetched.
 *
 * The timeline's version of R-08, with one addition this board needs more than the others: the
 * entries come from several systems, and whether *all* of them have reported is a different
 * question from whether this page has all the entries. The staleness list answers the first and
 * this sentence answers the second.
 */
export function fetchedNote(
  fetched: number | undefined,
  more: boolean,
  sources: readonly string[],
): string | undefined {
  if (fetched === undefined) return undefined;
  const held =
    `The count above is the ${String(fetched)} entries this page has fetched, not the case's ` +
    `total — SUP-03 publishes a cursor and no count.` +
    (sources.length === 0 ? "" : ` They came from ${sources.join(", ")}.`);
  return more
    ? `${held} Next fetches later entries from the last one shown; the cursor walks each source's own sequence, which a projection appends to and never rewrites, so no entry can cross it between pages.`
    : `${held} There are no later entries: the service answered no cursor, so this is all of them.`;
}

/**
 * The selected entry, for the panel beside the board.
 *
 * **The two subject lists are the point, and they are why this read needs no extra authorization.**
 * SUP-03 decides per entry what a caller may see, from the entry's own `participantSubjects` and
 * `authorizedAgentSubjects` — the projection has carried an access list on every row since it was
 * written, which is what ADR 0078 found when it published the public sibling of this read. Drawing
 * them is drawing why this row is on screen.
 *
 * **`sourceEventId` is the thing to quote.** `entryId` identifies the projection's row; the source
 * event identifies what actually happened, in the system it happened in, which is what somebody
 * asking that team will be asked for.
 */
export function timelineDetail(entry: TimelineEntry): RecordDetail {
  const state = stateOf(entry);

  return {
    headline: entry.summary,
    identifier: entry.entryId,
    source: "SUP-03",
    state: { ...state, code: "SUP-03", status: entry.category },
    fields: [
      field("System", entry.source),
      field("Happened", at(entry.occurredAt) ?? entry.occurredAt),
      ...optional("Reference", entry.reference, true),
      field("Source event", entry.sourceEventId, true),
      field("Sequence in its source", String(entry.sourceSequence)),
      field(
        "Participants",
        entry.participantSubjects.length === 0 ? "none" : entry.participantSubjects.join(", "),
        true,
      ),
      field(
        "Agents authorised",
        entry.authorizedAgentSubjects.length === 0
          ? "none"
          : entry.authorizedAgentSubjects.join(", "),
        true,
      ),
    ],
    note:
      "Rows from systems are read-only. SUP-03's two writes are a projection rebuild and a " +
      "projection append, and no reply operation exists anywhere on the platform.",
  };
}
